<template>
  <button
    :class="['base-button', type, { 'disabled': disabled }]"
    :disabled="disabled"
    @click="handleClick"
  >
    <font-awesome-icon
      v-if="loading"
      :icon="['fal', 'spinner']"
      size="lg"
      spin
    />
    <font-awesome-icon
      v-else-if="icon && iconStyle"
      :icon="[iconStyle, icon]"
      size="lg"
    />
    <slot />
  </button>
</template>

<script setup>
defineProps({
  icon: {
    type: String,
    required: false,
    validator: (value) => !value || value.trim().length > 0,
    default: null
  },
  iconStyle: {
    type: String,
    default: 'fal'
  },
  type: {
    type: String,
    default: 'default'
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click'])

function handleClick (event) {
  emit('click', event)
}
</script>

<style scoped>
.base-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.4em 0.8rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 6px;
  transition: background-color 0.2s, color 0.2s;
  font-weight: 500;
  line-height: 1.5;
}

.base-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.base-button.default {
  color: var( --color-accent);
}

.base-button.primary {
  color: var(--color-primary);
}

.base-button.delete {
  color: var(--color-danger);
}

.base-button.cancel {
  color: var(--color-secondary);
}

.base-button.info {
  color: var(--color-info);
}

.base-button.warning {
  color: var(--color-warning);
}

.base-button.danger {
  color: var(--color-danger);
}

/* Hover states */
.base-button.default:hover,
.base-button.cancel:hover {
  background-color: var( --color-accent);
  color: white;
}

.base-button.primary:hover {
  background-color: var(--color-primary);
  color: white;
}

.base-button.delete:hover {
  background-color: var(--color-danger);
  color: white;
}

.base-button.info:hover {
  background-color: var(--color-info);
  color: white;
}

.base-button.warning:hover {
  background-color: var(--color-warning);
  color: black;
}

.base-button.danger:hover {
  background-color: var(--color-danger);
  color: white;
}

.base-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.base-button.disabled:hover {
  background: none;
  color: inherit;
}

/* Add specific disabled hover states for each type */
.base-button.default.disabled:hover {
  color: var( --color-accent);
}

.base-button.primary.disabled:hover {
  color: var(--color-primary);
}

.base-button.danger.disabled:hover {
  color: var(--color-danger);
}
</style>

